// src/pages/AnalyticsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper, Typography, Box, Grid, Card, CardContent,
  Button, Stack, Chip, alpha, useTheme
} from '@mui/material';
import {
  TrendingUp, People, Business, Analytics,
  ShowChart, Timeline, Compare, Insights,
  LocalLaundryService, WaterDrop
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import { AnomalyDashboard } from '../features/Analytics/components/AnomalyDashboard';
import { useAnomalyDetection } from '../features/Analytics/hooks/useAnomalyDetection';

//АІ рекомендаціїї
import useAIRecommendations from '../features/AIRecommendations/hooks/useAIRecommendations';
import RecommendationsDashboard from '../features/AIRecommendations/components/RecommendationsDashboard';

// --- Безпечні кольори для теми ---
const getSafeColor = (theme, colorName) => {
  const colors = {
    primary: theme.palette.primary,
    success: theme.palette.success,
    warning: theme.palette.warning,
    info: theme.palette.info,
    secondary: theme.palette.secondary,
    error: theme.palette.error
  };
  
  const colorObj = colors[colorName] || theme.palette.primary;
  return {
    main: colorObj.main,
    dark: colorObj.dark || colorObj.main,
    light: colorObj.light || colorObj.main
  };
};

// --- Компоненти для дашборду ---

const MetricWidget = ({ title, value, change, icon: Icon, color = "primary", onClick }) => {
  const theme = useTheme();
  const safeColor = getSafeColor(theme, color);
  
  return (
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease',
        background: `linear-gradient(135deg, ${alpha(safeColor.main, 0.1)} 0%, ${alpha(safeColor.light, 0.05)} 100%)`,
        border: `1px solid ${alpha(safeColor.main, 0.2)}`,
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(safeColor.main, 0.15)}`,
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${safeColor.main} 0%, ${safeColor.dark} 100%)`,
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: safeColor.dark }}>
              {title}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {value}
              </Typography>
              {change && (
                <Chip 
                  label={change} 
                  size="small"
                  color={change.includes('+') ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const DashboardCard = ({ title, subtitle, children, action }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ flexGrow: 1 }}>
          {children}
        </Box>

        {action && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha('#000', 0.1)}` }}>
            {action}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// --- Основний компонент ---
export default function AnalyticsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);

  // Використання хука для виявлення аномалій
  const { 
    anomalyReport, 
    isLoading: anomaliesLoading, 
    error: anomaliesError 
  } = useAnomalyDetection();

  //АІ рекомендації
  const {
  recommendations,
  analysis: aiAnalysis,
  isLoading: aiLoading,
  applyRecommendation
} = useAIRecommendations();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const recs = await getData('lainsa_records');
      setRecords(Array.isArray(recs) ? recs : []);
      setLoading(false);
    })();
  }, []);

  // Аналіз даних для дашборду
  const dashboardData = useMemo(() => {
    const shiftReports = records.filter(r => r.recordType === 'shift_report');
    
    if (shiftReports.length === 0) {
      return {
        totalProduction: 0,
        productionChange: '+0%',
        activeClients: 0,
        activePrograms: 0,
        totalCycles: 0,
        dailyProduction: [0, 0, 0, 0, 0, 0, 0],
        forecast: 0,
        recentReportsCount: 0
      };
    }
    
    // Останні 30 днів для трендів
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    const recentReports = shiftReports.filter(r => new Date(r.fecha) >= last30Days);
    
    // Попередній період для порівняння
    const prevPeriodStart = new Date(last30Days);
    prevPeriodStart.setDate(prevPeriodStart.getDate() - 30);
    const prevReports = shiftReports.filter(r => {
      const date = new Date(r.fecha);
      return date >= prevPeriodStart && date < last30Days;
    });

    // Розрахунок метрик
    const currentProduction = recentReports.reduce((sum, r) => 
      sum + Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0), 0
    );
    
    const prevProduction = prevReports.reduce((sum, r) => 
      sum + Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0), 0
    );

    const productionChange = prevProduction > 0 
      ? `${((currentProduction - prevProduction) / prevProduction * 100).toFixed(1)}%`
      : currentProduction > 0 ? '+100%' : '+0%';

    // Унікальні клієнти (з вагою)
    const uniqueClients = new Set();
    recentReports.forEach(r => {
      (r.pesoCliente || []).forEach(item => {
        if (item.client && item.weight > 0) uniqueClients.add(item.client);
      });
    });

    // Активні програми тунелю
    const activePrograms = new Set();
    recentReports.forEach(r => {
      Object.keys(r.tunel || {}).forEach(program => {
        if (r.tunel[program] > 0) activePrograms.add(program);
      });
    });

    // Цикли пральних машин
    const totalCycles = recentReports.reduce((sum, record) => {
  let recordCycles = 0;
  
  try {
    // Рахуємо цикли з lav_ciclos
    if (record.lav_ciclos && typeof record.lav_ciclos === 'object') {
      Object.values(record.lav_ciclos).forEach(cyclesString => {
        if (cyclesString && typeof cyclesString === 'string') {
          const cyclesCount = Math.max(0, cyclesString.split('|').length - 1);
          recordCycles += cyclesCount;
        }
      });
    }
  } catch (error) {
    console.warn('Помилка при розрахунку циклів:', error);
    // Продовжуємо без додавання циклів для цього запису
  }
  
  return sum + recordCycles;
}, 0);

    // Прогноз на наступний тиждень (простий тренд)
    const dailyProduction = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      const dayReports = recentReports.filter(r => {
        const reportDate = new Date(r.fecha);
        return reportDate.toDateString() === date.toDateString();
      });
      
      const dayProduction = dayReports.reduce((sum, r) => 
        sum + Object.values(r.tunel || {}).reduce((s, v) => s + Number(v || 0), 0), 0
      );
      dailyProduction.push(Math.round(dayProduction));
    }

    // Простий прогноз (середнє останніх 3 днів)
    const last3Days = dailyProduction.slice(-3).filter(val => val > 0);
  const forecast = last3Days.length > 0 
    ? Math.round(last3Days.reduce((a, b) => a + b, 0) / last3Days.length)
    : 0;

  return {
    totalProduction: Math.round(currentProduction),
    productionChange,
    activeClients: uniqueClients.size,
    activePrograms: activePrograms.size,
    totalCycles, // Тепер це число, а не NaN
    dailyProduction,
    forecast,
    recentReportsCount: recentReports.length
  };
}, [records]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>{t('common_loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <Analytics sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              {t('analytics_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('analytics_subtitle', 'Огляд ключових показників та трендів виробництва')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Основні метрики */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title={t('analytics_tunnel_production', 'Виробництво')}
            value={`${(dashboardData.totalProduction / 1000).toFixed(1)}t`}
            change={dashboardData.productionChange}
            icon={TrendingUp}
            color="primary"
            onClick={() => navigate('/tunnel-analysis')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title={t('analytics_active_clients', 'Активні клієнти')}
            value={dashboardData.activeClients}
            change={dashboardData.activeClients > 0 ? `+${dashboardData.activeClients}` : undefined}
            icon={People}
            color="success"
            onClick={() => navigate('/client-analysis')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title={t('analytics_active_programs', 'Програми')}
            value={dashboardData.activePrograms}
            icon={Business}
            color="info"
            onClick={() => navigate('/tunnel-analysis')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricWidget
            title={t('analytics_total_cycles', 'Цикли')}
            value={dashboardData.totalCycles}
            change={dashboardData.totalCycles > 0 ? '+5%' : undefined}
            icon={LocalLaundryService}
            color="warning"
            onClick={() => navigate('/washer-analysis')}
          />
        </Grid>
      </Grid>

      {/* Виявлення аномалій */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {t('anomaly_detection_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('anomaly_detection_subtitle')}
              </Typography>
              
              <AnomalyDashboard 
                anomalyReport={anomalyReport}
                isLoading={anomaliesLoading}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* АІ рекомендації */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
  <Grid item xs={12}>
    <RecommendationsDashboard
      recommendations={recommendations}
      analysis={aiAnalysis}
      isLoading={aiLoading}
      onApplyRecommendation={applyRecommendation}
    />
  </Grid>
</Grid>

      {/* Розділи аналітики */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DashboardCard
            title={t('analytics_quick_navigation', 'Швидка навігація')}
            subtitle={t('analytics_navigate_to_details', 'Перейти до детального аналізу')}
          >
            <Stack spacing={2}>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<TrendingUp />}
                onClick={() => navigate('/tunnel-analysis')}
                size="large"
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {t('nav_analytics_tunnel')}
              </Button>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<People />}
                onClick={() => navigate('/client-analysis')}
                size="large"
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {t('nav_analytics_clients')}
              </Button>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<LocalLaundryService />}
                onClick={() => navigate('/washer-analysis')}
                size="large"
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {t('nav_analytics_washers')}
              </Button>
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<WaterDrop />}
                onClick={() => navigate('/meter-analysis')}
                size="large"
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {t('nav_analytics_meters')}
              </Button>
              
              {/* Кнопка прогнозу */}
              <Button 
                fullWidth 
                variant="outlined" 
                startIcon={<Insights />}
                onClick={() => navigate('/forecast')}
                size="large"
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
              >
                {t('forecast_title', 'Детальний прогноз')}
              </Button>
            </Stack>
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardCard
            title={t('analytics_production_forecast', 'Прогноз виробництва')}
            subtitle={t('analytics_next_week_forecast', 'Наступний тиждень')}
            action={
              <Button startIcon={<Insights />} variant="text" onClick={() => navigate('/forecast')}>
                {t('analytics_detailed_forecast', 'Детальний прогноз')}
              </Button>
            }
          >
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', mb: 1 }}>
                {dashboardData.forecast} {t('labels_kg')}/día
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('analytics_based_on_trends', 'На основі трендів останніх 7 днів')}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                {dashboardData.dailyProduction.map((prod, index) => (
                  <Box key={index} sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {prod}{t('labels_kg')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('analytics_day', 'Д')}-{index + 1}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </DashboardCard>
        </Grid>

        <Grid item xs={12}>
          <DashboardCard
            title={t('analytics_period_comparison', 'Порівняння періодів')}
            subtitle={t('analytics_current_vs_previous', 'Поточний vs Попередній місяць')}
            action={
              <Button startIcon={<Compare />} variant="text">
                {t('analytics_detailed_comparison', 'Детальне порівняння')}
              </Button>
            }
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>{t('analytics_current_month', 'Поточний місяць')}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {(dashboardData.totalProduction / 1000).toFixed(1)} {t('labels_ton', 'т')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dashboardData.recentReportsCount} {t('analytics_reports_count', 'звітів')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>{t('analytics_previous_month', 'Попередній місяць')}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                    {dashboardData.productionChange !== '+0%' ? 
                      `${((dashboardData.totalProduction / (1 + parseFloat(dashboardData.productionChange) / 100)) / 1000).toFixed(1)} ${t('labels_ton', 'т')}` :
                      `0 ${t('labels_ton', 'т')}`
                    }
                  </Typography>
                  <Chip 
                    label={dashboardData.productionChange} 
                    color={dashboardData.productionChange.includes('+') ? 'success' : 'error'}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
}


